// src/components/AddWorkoutDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    DialogContentText,
    Alert,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { addCompetitionWorkout, updateCompetitionWorkout } from '../api/competitionApi';
import TextareaAutosize from "@mui/material/TextareaAutosize";
export default function AddWorkoutDialog({ open, onClose, competitionId, categories, workout }) {
    const [formData, setFormData] = useState({
        name: '',
        resultType: 'TIME',
        workoutDescriptions: {},
        tiebreak: false,
        tiebreakType: null,
        tiebreakLowerBetter: true
    });
    const [selectedCategory, setSelectedCategory] = useState(0);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (workout) {
            setFormData({
                name: workout.name,
                resultType: workout.resultType,
                workoutDescriptions: workout.workoutDescriptions,
                tiebreak: workout.tiebreak || false,
                tiebreakType: workout.tiebreakType || null,
                tiebreakLowerBetter: workout.tiebreakLowerBetter !== undefined ? workout.tiebreakLowerBetter : true
            });
        } else {
            // Initialize empty descriptions for all categories
            const descriptions = {};
            categories.forEach(cat => {
                descriptions[cat] = '';
            });
            setFormData({
                name: '',
                resultType: 'TIME',
                workoutDescriptions: descriptions,
                tiebreak: false,
                tiebreakType: null,
                tiebreakLowerBetter: true
            });
        }
    }, [workout, categories, open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDescriptionChange = (category, value) => {
        setFormData(prev => ({
            ...prev,
            workoutDescriptions: {
                ...prev.workoutDescriptions,
                [category]: value
            }
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.resultType) {
            setAlertMessage('Please fill in all required fields');
            setAlertOpen(true);
            return;
        }

        // Check if tiebreak is selected but tiebreakType is not
        if (formData.tiebreak && !formData.tiebreakType) {
            setAlertMessage('Please select tiebreak type (Time or Number)');
            setAlertOpen(true);
            return;
        }

        // Check if at least one description is filled
        const hasDescription = Object.values(formData.workoutDescriptions).some(desc => desc.trim() !== '');
        if (!hasDescription) {
            setAlertMessage('Please add at least one workout description');
            setAlertOpen(true);
            return;
        }

        try {
            setErrorMessage('');
            if (workout) {
                await updateCompetitionWorkout(workout.id, formData);
            } else {
                await addCompetitionWorkout(competitionId, formData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving workout:', error);
            setErrorMessage('Failed to save workout');
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        setAlertMessage('');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {workout ? 'Edit Workout' : 'Add New Workout'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Workout Name *"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="e.g., Fran, Murph, Hero WOD"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Result Type *</InputLabel>
                            <Select
                                value={formData.resultType}
                                onChange={(e) => handleChange('resultType', e.target.value)}
                                label="Result Type *"
                            >
                                <MenuItem value="TIME">Time (hh:mm:ss)</MenuItem>
                                <MenuItem value="REPS">Repetitions</MenuItem>
                                <MenuItem value="WEIGHT">Weight (kg)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.tiebreak}
                                    onChange={(e) => handleChange('tiebreak', e.target.checked)}
                                />
                            }
                            label="Tiebreak Workout"
                        />
                    </Grid>

                    {formData.tiebreak && (
                        <>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Tiebreak Type *</InputLabel>
                                    <Select
                                        value={formData.tiebreakType || ''}
                                        onChange={(e) => handleChange('tiebreakType', e.target.value)}
                                        label="Tiebreak Type *"
                                    >
                                        <MenuItem value="TIME">Time</MenuItem>
                                        <MenuItem value="NUMBER">Number</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Tiebreak Result Type</InputLabel>
                                    <Select
                                        value={formData.tiebreakLowerBetter ? 'lower' : 'higher'}
                                        onChange={(e) => handleChange('tiebreakLowerBetter', e.target.value === 'lower')}
                                        label="Tiebreak Result Type"
                                    >
                                        <MenuItem value="lower">Lower value is better</MenuItem>
                                        <MenuItem value="higher">Higher value is better</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Workout Descriptions by Category
                        </Typography>

                        <Tabs
                            value={selectedCategory}
                            onChange={(e, newValue) => setSelectedCategory(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {categories.map((category, idx) => (
                                <Tab key={idx} label={category} />
                            ))}
                        </Tabs>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Description for {categories[selectedCategory]}
                            </Typography>
                            <TextareaAutosize
                                minRows={5}
                                value={formData.workoutDescriptions[categories[selectedCategory]] || ''}
                                onChange={(e) => handleDescriptionChange(categories[selectedCategory], e.target.value)}
                                placeholder="Enter workout description, movements, time caps, etc."
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    borderColor: '#AAAAAA',
                                    fontSize: '14px',
                                    lineHeight: '1.5',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit}>
                    {workout ? 'Update' : 'Add'} Workout
                </Button>
            </DialogActions>

            {/* Alert Dialog */}
            <Dialog
                open={alertOpen}
                onClose={handleAlertClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Warning
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="alert-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleAlertClose}
                        color="primary"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}